import { Link } from 'react-router-dom';
import { 
  Database, Cloud, Settings, BarChart3, Users, 
  ArrowRight, CheckCircle, Zap, Shield, Clock, Target
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import SEO from '@/components/SEO';

const services = [
  {
    id: 'erp',
    icon: Database,
    title: 'Oracle ERP Implementation',
    subtitle: 'Ready-to-Deploy & Customized Solutions',
    description: 'Transform your enterprise operations with our comprehensive Oracle ERP implementation services. We deliver both rapid-deployment solutions and fully customized implementations tailored to your unique business requirements.',
    features: [
      'Oracle Cloud ERP (Fusion)',
      'Oracle E-Business Suite',
      'Oracle JD Edwards',
      'Oracle NetSuite',
      'Custom module development',
      'Data migration & integration'
    ],
    benefits: [
      { icon: Zap, text: '40% faster implementation' },
      { icon: Shield, text: 'Enterprise-grade security' },
      { icon: Clock, text: '24/7 go-live support' },
      { icon: Target, text: 'ROI-focused approach' }
    ],
    stats: [
      { value: '200+', label: 'ERP Implementations' },
      { value: '99.9%', label: 'Go-Live Success' },
      { value: '42%', label: 'Avg Cost Reduction' }
    ]
  },
  {
    id: 'custom',
    icon: Settings,
    title: 'Custom Oracle Applications',
    subtitle: 'Bespoke Solutions for Unique Challenges',
    description: 'Build powerful, tailored applications that address your specific business challenges. Our expert developers leverage Oracle APEX, PL/SQL, and modern frameworks to create solutions that drive efficiency and innovation.',
    features: [
      'Oracle APEX development',
      'PL/SQL & database solutions',
      'REST API development',
      'Mobile-responsive applications',
      'Integration middleware',
      'Reporting & analytics dashboards'
    ],
    benefits: [
      { icon: Zap, text: 'Rapid prototyping' },
      { icon: Shield, text: 'Oracle-native security' },
      { icon: Clock, text: 'Agile delivery' },
      { icon: Target, text: 'Business-aligned design' }
    ],
    stats: [
      { value: '100+', label: 'Custom Apps Built' },
      { value: '85%', label: 'Process Automation' },
      { value: '$10M+', label: 'Client Savings' }
    ]
  },
  {
    id: 'cloud',
    icon: Cloud,
    title: 'Cloud Migration',
    subtitle: 'Seamless Transition to Oracle Cloud',
    description: 'Migrate your on-premise Oracle systems to Oracle Cloud Infrastructure (OCI) with zero downtime and complete data integrity. Our proven methodology ensures a smooth transition while optimizing costs and performance.',
    features: [
      'Oracle Cloud Infrastructure (OCI)',
      'Database cloud migration',
      'Application modernization',
      'Hybrid cloud architecture',
      'Performance optimization',
      'Cost analysis & optimization'
    ],
    benefits: [
      { icon: Zap, text: 'Zero-downtime migration' },
      { icon: Shield, text: 'Enhanced compliance' },
      { icon: Clock, text: 'Accelerated timelines' },
      { icon: Target, text: '60% cost reduction' }
    ],
    stats: [
      { value: '150+', label: 'Cloud Migrations' },
      { value: '100%', label: 'Data Integrity' },
      { value: '0', label: 'Downtime Events' }
    ]
  },
  {
    id: 'managed',
    icon: BarChart3,
    title: 'Managed Services',
    subtitle: '24/7 Oracle System Support',
    description: 'Keep your Oracle environment running at peak performance with our comprehensive managed services. Our dedicated team provides proactive monitoring, regular optimization, and rapid incident response around the clock.',
    features: [
      '24/7 system monitoring',
      'Performance optimization',
      'Security patch management',
      'Backup & disaster recovery',
      'Quarterly health checks',
      'Dedicated support team'
    ],
    benefits: [
      { icon: Zap, text: '4-hour resolution SLA' },
      { icon: Shield, text: 'Proactive security' },
      { icon: Clock, text: 'Round-the-clock support' },
      { icon: Target, text: '99.95% uptime guarantee' }
    ],
    stats: [
      { value: '99.95%', label: 'SLA Compliance' },
      { value: '35%', label: 'Incident Reduction' },
      { value: '4hr', label: 'Avg Resolution Time' }
    ]
  },
  {
    id: 'staffing',
    icon: Users,
    title: 'IT Staffing & Augmentation',
    subtitle: 'Expert Oracle Professionals On Demand',
    description: 'Access our network of certified Oracle professionals for your project and operational needs. From short-term projects to long-term engagements, we provide the expertise you need when you need it.',
    features: [
      'Oracle-certified consultants',
      'Project-based engagement',
      'Long-term augmentation',
      'Technical architects',
      'Database administrators',
      'Application developers'
    ],
    benefits: [
      { icon: Zap, text: 'Rapid deployment' },
      { icon: Shield, text: 'Vetted professionals' },
      { icon: Clock, text: 'Flexible engagement' },
      { icon: Target, text: 'Skill-matched talent' }
    ],
    stats: [
      { value: '300+', label: 'Oracle Experts' },
      { value: '95%', label: 'Client Satisfaction' },
      { value: '48hr', label: 'Avg Placement Time' }
    ]
  }
];

export default function ServicesPage() {
  return (
    <div className="pt-20" data-testid="services-page">
      <SEO 
        title="Oracle Services"
        description="Comprehensive Oracle services including ERP Implementation, Custom Application Development, Cloud Migration, and Managed Services for enterprise transformation."
        keywords="Oracle Services, ERP Implementation, Cloud Migration, Custom Oracle Apps, Managed Services, Oracle Consulting"
      />
      {/* Hero Section */}
      <section className="section-dark py-20 md:py-32">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="eyebrow mb-4 inline-block">Our Services</span>
            <h1 className="heading-xl text-white mb-6 font-heading">
              End-to-End Oracle
              <span className="block text-oracle-red">Solutions</span>
            </h1>
            <p className="text-slate-300 font-body text-lg md:text-xl leading-relaxed">
              From implementation to optimization, we deliver comprehensive Oracle solutions that transform your enterprise operations and drive measurable business outcomes.
            </p>
          </div>
        </div>
      </section>

      {/* Services Tabs Section */}
      <section className="section-light py-12 md:py-20 lg:py-32" data-testid="services-tabs">
        <div className="container-custom">
          <Tabs defaultValue="erp" className="w-full">
            <div className="overflow-x-auto -mx-4 px-4 pb-2 md:overflow-visible md:mx-0 md:px-0">
              <TabsList className="flex flex-nowrap md:flex-wrap justify-start gap-2 bg-transparent mb-8 md:mb-12 h-auto min-w-max md:min-w-0">
                {services.map((service) => (
                  <TabsTrigger 
                    key={service.id}
                    value={service.id}
                    className="data-[state=active]:bg-oracle-red data-[state=active]:text-white bg-white border border-slate-200 px-3 md:px-6 py-2 md:py-3 font-body text-xs md:text-sm rounded-sm whitespace-nowrap"
                    data-testid={`tab-${service.id}`}
                  >
                    <service.icon className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                    {service.title.split(' ').slice(-1)[0]}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {services.map((service) => (
              <TabsContent key={service.id} value={service.id} id={service.id}>
                <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
                  {/* Left Column - Content */}
                  <div>
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 mb-4 md:mb-6">
                      <div className="w-12 h-12 md:w-16 md:h-16 bg-oracle-red/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <service.icon className="w-6 h-6 md:w-8 md:h-8 text-oracle-red" />
                      </div>
                      <div>
                        <h2 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-slate-900">
                          {service.title}
                        </h2>
                        <p className="font-body text-slate-600 text-sm md:text-base">{service.subtitle}</p>
                      </div>
                    </div>
                    
                    <p className="font-body text-slate-700 text-sm md:text-base leading-relaxed mb-6 md:mb-8">
                      {service.description}
                    </p>

                    {/* Features List */}
                    <h3 className="font-heading text-base md:text-lg font-bold text-slate-900 mb-3 md:mb-4">
                      Capabilities
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3 mb-6 md:mb-8">
                      {service.features.map((feature) => (
                        <div key={feature} className="flex items-center space-x-2">
                          <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-oracle-red flex-shrink-0" />
                          <span className="font-body text-xs md:text-sm text-slate-700">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* Benefits */}
                    <div className="grid grid-cols-2 gap-2 md:gap-4 mb-6 md:mb-8">
                      {service.benefits.map((benefit, index) => (
                        <div 
                          key={index}
                          className="flex items-center space-x-2 md:space-x-3 bg-slate-50 p-2 md:p-4 rounded-sm"
                        >
                          <benefit.icon className="w-4 h-4 md:w-5 md:h-5 text-oracle-red flex-shrink-0" />
                          <span className="font-body text-xs md:text-sm text-slate-700">{benefit.text}</span>
                        </div>
                      ))}
                    </div>

                    <Link to="/contact">
                      <Button 
                        className="w-full sm:w-auto bg-oracle-red hover:bg-oracle-red-dark text-white font-body"
                        data-testid={`service-cta-${service.id}`}
                      >
                        Get Started
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </div>

                  {/* Right Column - Stats Card */}
                  <div className="mt-6 lg:mt-0">
                    <Card className="card-dark">
                      <CardHeader className="p-4 md:p-6">
                        <CardTitle className="font-heading text-lg md:text-xl text-white">
                          Results That Matter
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-4 md:p-6 pt-0">
                        <div className="grid gap-4 md:gap-6">
                          {service.stats.map((stat, index) => (
                            <div 
                              key={index}
                              className="flex items-center justify-between border-b border-slate-700 pb-3 md:pb-4 last:border-0 last:pb-0"
                            >
                              <span className="font-body text-slate-400 text-sm">{stat.label}</span>
                              <span className="font-heading text-2xl md:text-3xl font-bold text-oracle-red">
                                {stat.value}
                              </span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Trust Indicators */}
                    <div className="mt-6 md:mt-8 bg-slate-50 p-4 md:p-6 rounded-sm">
                      <h4 className="font-heading text-base md:text-lg font-bold text-slate-900 mb-3 md:mb-4">
                        Why Choose Trid?
                      </h4>
                      <ul className="space-y-2 md:space-y-3">
                        <li className="flex items-start space-x-2 md:space-x-3">
                          <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-oracle-red flex-shrink-0 mt-0.5" />
                          <span className="font-body text-xs md:text-sm text-slate-700">
                            Oracle Certified Implementation Partner
                          </span>
                        </li>
                        <li className="flex items-start space-x-2 md:space-x-3">
                          <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-oracle-red flex-shrink-0 mt-0.5" />
                          <span className="font-body text-xs md:text-sm text-slate-700">
                            10+ years of enterprise experience
                          </span>
                        </li>
                        <li className="flex items-start space-x-2 md:space-x-3">
                          <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-oracle-red flex-shrink-0 mt-0.5" />
                          <span className="font-body text-xs md:text-sm text-slate-700">
                            100+ successful implementations
                          </span>
                        </li>
                        <li className="flex items-start space-x-2 md:space-x-3">
                          <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-oracle-red flex-shrink-0 mt-0.5" />
                          <span className="font-body text-xs md:text-sm text-slate-700">
                            98% client retention rate
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-dark py-12 md:py-20 lg:py-32" data-testid="services-cta">
        <div className="container-custom text-center">
          <span className="eyebrow mb-3 md:mb-4 inline-block text-xs">Ready to Get Started?</span>
          <h2 className="heading-lg text-white mb-4 md:mb-6 font-heading max-w-2xl mx-auto">
            Transform Your Enterprise with Oracle Excellence
          </h2>
          <p className="text-slate-300 font-body text-sm md:text-base lg:text-lg mb-6 md:mb-8 max-w-xl mx-auto">
            Schedule a consultation to discuss your requirements and discover how we can accelerate your digital transformation.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
            <Link to="/contact" className="w-full sm:w-auto">
              <Button 
                size="lg" 
                className="w-full sm:w-auto bg-oracle-red hover:bg-oracle-red-dark text-white font-body px-6 md:px-8 py-5 md:py-6 text-sm md:text-base"
              >
                Schedule Consultation
              </Button>
            </Link>
            <Link to="/case-studies" className="w-full sm:w-auto">
              <Button 
                size="lg" 
                variant="outline" 
                className="w-full sm:w-auto border-white text-white hover:bg-white hover:text-slate-900 font-body px-6 md:px-8 py-5 md:py-6 text-sm md:text-base"
              >
                View Success Stories
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
